import type { NewRequestInput } from '@klukka/client';
import { fmtDay, fmtH, LANGS, t, type DayRow, type LangCode, type RequestType } from '@klukka/core';
import { useState, type ReactNode } from 'react';

type Tr = (k: Parameters<typeof t>[1]) => string;

function Sheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className="sheet-bg" onClick={onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        {children}
      </div>
    </div>
  );
}

export function LanguageSheet({ lang, tr, onPick, onClose }: {
  lang: LangCode;
  tr: Tr;
  onPick: (l: LangCode) => void;
  onClose: () => void;
}) {
  return (
    <Sheet onClose={onClose}>
      <div className="card-title">{tr('language')}</div>
      <div className="lang-grid">
        {LANGS.map(([code, name]) => (
          <button key={code} className={`lang-opt${lang === code ? ' on' : ''}`} onClick={() => onPick(code)}>
            {name}
          </button>
        ))}
      </div>
    </Sheet>
  );
}

/** Signing the period's timesheet — the prerequisite for payout. */
export function SignSheet({ tr, name, hours, periodLabel, busy, onSubmit, onClose }: {
  tr: Tr;
  name: string;
  hours: number;
  periodLabel: string;
  busy: boolean;
  onSubmit: (signature: string) => void;
  onClose: () => void;
}) {
  const [signature, setSignature] = useState('');
  return (
    <Sheet onClose={onClose}>
      <div className="card-title">{tr('signTitle')} — {fmtH(hours)} {tr('hours')}</div>
      <div className="dimtx" style={{ marginBottom: 8, fontSize: 13 }}>{periodLabel}</div>
      <div className="dimtx" style={{ marginBottom: 8, fontSize: 13 }}>{tr('signHint')}</div>
      <input className="inp sig" placeholder={name} value={signature} onChange={(e) => setSignature(e.target.value)} />
      <div className="sheet-btns">
        <button className="btn-ghost" onClick={onClose}>{tr('cancel')}</button>
        <button
          className="btn-main"
          style={{ flex: 1 }}
          disabled={busy || !signature.trim()}
          onClick={() => onSubmit(signature.trim())}
        >
          {tr('send')}
        </button>
      </div>
    </Sheet>
  );
}

export function EditHoursSheet({ tr, day, busy, onSave, onClose }: {
  tr: Tr;
  day: DayRow;
  busy: boolean;
  onSave: (hours: number) => void;
  onClose: () => void;
}) {
  const [hours, setHours] = useState(day.h);
  return (
    <Sheet onClose={onClose}>
      <div className="card-title">{tr('editHours')} — {fmtDay(day.date)}</div>
      <div className="stepper">
        <button onClick={() => setHours((v) => Math.max(0, v - 0.5))} aria-label="−0,5">−</button>
        <div className="stepper-v">{fmtH(hours)} <span>{tr('hours')}</span></div>
        <button onClick={() => setHours((v) => Math.min(16, v + 0.5))} aria-label="+0,5">+</button>
      </div>
      <div className="sheet-btns">
        <button className="btn-ghost" onClick={onClose}>{tr('cancel')}</button>
        <button className="btn-main" style={{ flex: 1 }} disabled={busy} onClick={() => onSave(hours)}>{tr('saveBtn')}</button>
      </div>
    </Sheet>
  );
}

export function RequestSheet({ tr, today, busy, onSubmit, onClose }: {
  tr: Tr;
  today: string;
  busy: boolean;
  onSubmit: (input: NewRequestInput) => void;
  onClose: () => void;
}) {
  const [type, setType] = useState<RequestType>('vacation');
  const [from, setFrom] = useState(today);
  const [fromTime, setFromTime] = useState('00:00');
  const [to, setTo] = useState(today);
  const [toTime, setToTime] = useState('23:59');
  const [note, setNote] = useState('');

  const submit = () => {
    if (type === 'open' && !note.trim()) return;
    onSubmit({
      type,
      from: type === 'open' ? today : from,
      fromTime,
      to: type === 'open' ? today : to,
      toTime,
      note,
    });
  };

  const types: Array<[RequestType, string]> = [
    ['vacation', tr('vacation')], ['sick', tr('sick')], ['leave', tr('leave')], ['open', tr('openReq')],
  ];

  return (
    <Sheet onClose={onClose}>
      <div className="card-title">{tr('newRequest')}</div>
      <div className="seg3">
        {types.map(([k, label]) => (
          <button
            key={k}
            className={type === k ? 'on' : ''}
            onClick={() => setType(k)}
            style={{ fontSize: 12, padding: '9px 2px' }}
          >
            {label}
          </button>
        ))}
      </div>

      {type !== 'open' ? (
        <div>
          <div className="split" style={{ marginTop: 10 }}>
            <label className="fld">{tr('from')}<input type="date" className="inp" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
            <label className="fld">{tr('timeLbl')}<input type="time" className="inp" value={fromTime} onChange={(e) => setFromTime(e.target.value)} /></label>
          </div>
          <div className="split">
            <label className="fld">{tr('to')}<input type="date" className="inp" value={to} onChange={(e) => setTo(e.target.value)} /></label>
            <label className="fld">{tr('timeLbl')}<input type="time" className="inp" value={toTime} onChange={(e) => setToTime(e.target.value)} /></label>
          </div>
        </div>
      ) : null}

      <label className="fld">
        {tr('note')}
        <input className="inp" placeholder={type === 'open' ? tr('openHint') : ''} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>

      <div className="sheet-btns">
        <button className="btn-ghost" onClick={onClose}>{tr('cancel')}</button>
        <button className="btn-main" style={{ flex: 1 }} disabled={busy} onClick={submit}>{tr('send')}</button>
      </div>
    </Sheet>
  );
}
